import type { Event, ProgramEventStatus, ProgramListEvent, ProgramUserRsvp } from 'models';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useUser } from 'features/auth';
import { useEventsInfiniteQuery, useNodeEventsInfiniteQuery } from 'features/events';
import type { EventTimelineType } from 'features/events';

type P = {
  rsvp: ProgramUserRsvp[];
  status?: ProgramEventStatus;
  type: EventTimelineType;
};

const PAGE_SIZE = 10;
const VISIBLE_PAGE_SIZE = 8;

export type EventWithSource = NodeEventWithSource | RailsEventWithSource;

type NodeEventWithSource = {
  event: Event;
  source: 'node';
};

type RailsEventWithSource = {
  event: ProgramListEvent;
  source: 'rails';
};

const countVisibleSources = (events?: EventWithSource[]) => {
  const counts = { node: 0, rails: 0 };

  for (const item of events ?? []) {
    counts[item.source]++;
  }

  return counts;
};

export const useGetEvents = ({ rsvp, status, type }: P) => {
  const { nodeUserData } = useUser();
  const [visibleCount, setVisibleCount] = useState(VISIBLE_PAGE_SIZE);

  const {
    data: railsData,
    fetchNextPage: fetchNextPageRails,
    hasNextPage: hasNextPageRails,
    isFetchingNextPage: isFetchingNextPageRails,
    isLoading: isLoadingRails,
    isRefetching: isRefetchingRails,
  } = useEventsInfiniteQuery({
    filter: { rsvp, status, type },
    pagination: { pageSize: PAGE_SIZE },
  });

  const {
    data: nodeData,
    fetchNextPage: fetchNextPageNode,
    hasNextPage: hasNextPageNode,
    isFetchingNextPage: isFetchingNextPageNode,
    isLoading: isLoadingNode,
    isRefetching: isRefetchingNode,
  } = useNodeEventsInfiniteQuery({
    filter: {
      rsvpStatus: rsvp,
      type,
      userId: nodeUserData._id,
    },
    pagination: { pageSize: PAGE_SIZE },
  });

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect -- TODO: fix later
    setVisibleCount(VISIBLE_PAGE_SIZE);
  }, [type]);

  const railsEvents = useMemo(
    () => railsData?.pages.flat().map((event) => ({ event, source: 'rails' as const })),
    [railsData],
  );

  const nodeEvents = useMemo(
    () => nodeData?.pages.flatMap((page) => page.data).map((event) => ({ event, source: 'node' as const })),
    [nodeData],
  );

  const mergedEvents = useMemo(
    () =>
      railsEvents && nodeEvents
        ? // eslint-disable-next-line array-callback-return -- switch is exhaustive so we always return a value
          [...railsEvents, ...nodeEvents].toSorted((a, b) => {
            switch (type) {
              case 'past':
                return b.event.startTime.getTime() - a.event.startTime.getTime();
              case 'upcoming':
                return a.event.startTime.getTime() - b.event.startTime.getTime();
            }
          })
        : undefined,
    [railsEvents, nodeEvents, type],
  );

  const visibleEvents = useMemo(() => mergedEvents?.slice(0, visibleCount), [mergedEvents, visibleCount]);

  const stats = useMemo(() => {
    const used = countVisibleSources(visibleEvents);

    const railsLoaded = railsEvents?.length ?? 0;
    const nodeLoaded = nodeEvents?.length ?? 0;

    return {
      nodeLoaded,
      railsLoaded,
      remainingNode: nodeLoaded - used.node,
      remainingRails: railsLoaded - used.rails,
      usedNode: used.node,
      usedRails: used.rails,
    };
  }, [visibleEvents, railsEvents?.length, nodeEvents?.length]);

  const fetchNextPage = useCallback(() => {
    const shouldFetchRails = stats.remainingRails < VISIBLE_PAGE_SIZE && hasNextPageRails;
    const shouldFetchNode = stats.remainingNode < VISIBLE_PAGE_SIZE && hasNextPageNode;

    if (shouldFetchRails) {
      void fetchNextPageRails();
    }

    if (shouldFetchNode) {
      void fetchNextPageNode();
    }

    setVisibleCount((previous) => previous + VISIBLE_PAGE_SIZE);
  }, [
    stats.remainingRails,
    stats.remainingNode,
    hasNextPageRails,
    hasNextPageNode,
    fetchNextPageRails,
    fetchNextPageNode,
  ]);

  const hasMoreVisibleItems = useMemo(() => {
    if (visibleCount < (mergedEvents?.length ?? 0)) {
      return true;
    }

    return hasNextPageRails || hasNextPageNode;
  }, [visibleCount, mergedEvents, hasNextPageRails, hasNextPageNode]);

  return useMemo(
    () => ({
      events: visibleEvents,
      fetchNextPage,
      hasNextPage: hasMoreVisibleItems,
      isFetchingNextPage: isFetchingNextPageRails || isFetchingNextPageNode,
      isLoading: isLoadingRails || isLoadingNode,
      isRefetching: isRefetchingRails || isRefetchingNode,
    }),
    [
      visibleEvents,
      fetchNextPage,
      hasMoreVisibleItems,
      isFetchingNextPageRails,
      isFetchingNextPageNode,
      isLoadingRails,
      isLoadingNode,
      isRefetchingRails,
      isRefetchingNode,
    ],
  );
};
